import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { localLogger } from '@/lib/local-logger';
import { isSupabaseTransportError } from '@/lib/resilient-fetch';

/**
 * Generates a unique instance ID for this browser tab
 * Persisted in sessionStorage so it remains consistent during the session
 */
function getInstanceId(userId: string): string {
  const storageKey = `processing_instance_id_${userId}`;

  let instanceId = sessionStorage.getItem(storageKey);

  if (!instanceId) {
    // Generate new instance ID: {userId}_{randomTabId}
    const tabId = Math.random().toString(36).substring(2, 11);
    instanceId = `${userId}_${tabId}`;
    sessionStorage.setItem(storageKey, instanceId);
  }

  return instanceId;
}

export interface ProcessingLock {
  id: number;
  recording_id: string;
  user_id: string;
  processing_instance_id: string;
  acquired_at: string;
  expires_at: string;
}

/**
 * DistributedLockManager: Coordinates queue processing across browser tabs
 *
 * Prevents:
 * - Multiple tabs processing the same recording
 * - Duplicate API calls and duplicate transcription jobs
 * - Race conditions in offline recording sync
 *
 * Uses:
 * - Supabase database with atomic UNIQUE constraint
 * - TTL-based automatic lock expiration (5 minutes)
 * - Heartbeat cleanup for stale locks
 */
export class DistributedLockManager {
  private static instance: DistributedLockManager;
  private activeLocks = new Map<string, string>(); // recordingId -> instanceId
  private lockTimeoutMs = 5 * 60 * 1000; // 5 minutes
  private cleanupIntervalMs = 60 * 1000; // 1 minute cleanup check
  private cleanupTimer: ReturnType<typeof setInterval> | null = null;

  private constructor() {}

  public static getInstance(): DistributedLockManager {
    if (!DistributedLockManager.instance) {
      DistributedLockManager.instance = new DistributedLockManager();
    }
    return DistributedLockManager.instance;
  }

  /**
   * Start automatic cleanup of expired locks
   */
  startCleanup(): void {
    if (this.cleanupTimer) return; // Already running

    this.cleanupTimer = setInterval(() => {
      this.cleanupExpiredLocks().catch(err => {
        logger.error('Failed to cleanup expired locks:', err);
      });
    }, this.cleanupIntervalMs);

    logger.info('Started distributed lock cleanup service');
  }

  /**
   * Stop automatic cleanup
   */
  stopCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
      logger.info('Stopped distributed lock cleanup service');
    }
  }

  /**
   * Attempt to acquire a lock for processing a recording
   *
   * Uses atomic UNIQUE constraint to ensure only one instance acquires the lock
   *
   * @param recordingId - Recording ID to lock
   * @param userId - User ID
   * @returns true if lock acquired, false if already locked by another instance
   */
  async acquireLock(recordingId: string, userId: string): Promise<boolean> {
    const instanceId = getInstanceId(userId);
    const expiresAt = new Date(Date.now() + this.lockTimeoutMs).toISOString();

    try {
      logger.info(`Attempting to acquire lock for recording ${recordingId}...`);

      // Atomic INSERT with UNIQUE constraint
      // If recording_id already exists, this will fail with unique constraint violation
      const { data, error } = await supabase
        .from('processing_locks')
        .insert({
          recording_id: recordingId,
          user_id: userId,
          processing_instance_id: instanceId,
          expires_at: expiresAt
        })
        .select();

      if (error) {
        if (error.code === '23505') {
          // Unique constraint violation - lock already held
          logger.warn(`Recording ${recordingId} is already locked by another instance`);
          return false;
        }

        throw error;
      }

      if (!data || data.length === 0) {
        logger.warn(`Failed to insert lock for recording ${recordingId}`);
        return false;
      }

      // Lock acquired successfully
      this.activeLocks.set(recordingId, instanceId);
      logger.info(`✅ Acquired lock for recording ${recordingId}`);

      return true;
    } catch (error) {
      logger.error(`Failed to acquire lock for recording ${recordingId}:`, error);
      localLogger.captureError(error as Error, {
        component: 'distributed_lock_manager',
        operation: 'acquire_lock',
        metadata: { recordingId }
      });

      return false;
    }
  }

  /**
   * Release a lock for a recording
   *
   * @param recordingId - Recording ID to unlock
   * @param userId - User ID
   * @returns true if lock released, false if lock not found or didn't belong to this instance
   */
  async releaseLock(recordingId: string, userId: string): Promise<boolean> {
    const instanceId = getInstanceId(userId);

    try {
      logger.info(`Releasing lock for recording ${recordingId}...`);

      // Only delete if it's our lock
      const { error } = await supabase
        .from('processing_locks')
        .delete()
        .eq('recording_id', recordingId)
        .eq('processing_instance_id', instanceId);

      if (error) {
        throw error;
      }

      this.activeLocks.delete(recordingId);
      logger.info(`✅ Released lock for recording ${recordingId}`);

      return true;
    } catch (error) {
      logger.error(`Failed to release lock for recording ${recordingId}:`, error);
      localLogger.captureError(error as Error, {
        component: 'distributed_lock_manager',
        operation: 'release_lock',
        metadata: { recordingId }
      });

      return false;
    }
  }

  /**
   * Check if a recording is locked
   *
   * @param recordingId - Recording ID
   * @returns Lock details if locked, null if not locked
   */
  async isLocked(recordingId: string): Promise<ProcessingLock | null> {
    try {
      // Suppress console.error during Supabase call to prevent harmless 406/403 spam
      const originalError = console.error;
      let suppressedError: any = null;

      console.error = (...args: any[]) => {
        // Capture but don't log 406/403 errors from Supabase
        const errorMsg = String(args[0]);
        if (!errorMsg.includes('406') && !errorMsg.includes('403')) {
          originalError(...args);
        }
        if (errorMsg.includes('406') || errorMsg.includes('403')) {
          suppressedError = args[0];
        }
      };

      try {
        // The HTTP status is on the response, not on PostgrestError — reading
        // error.status gave undefined, so the 406/403 suppression below never
        // actually fired and these harmless responses were rethrown and logged.
        const { data, error, status } = await supabase
          .from('processing_locks')
          .select('*')
          .eq('recording_id', recordingId)
          .maybeSingle();

        if (error) {
          // 406/403 = transient RLS state issues, safe to ignore
          if (status === 406 || status === 403) {
            return null;
          }
          throw error;
        }

        return data as ProcessingLock | null;
      } finally {
        console.error = originalError;
      }
    } catch (error: any) {
      // Only log non-406/403 errors; these are known harmless issues with transient RLS state
      if (error?.status !== 406 && error?.status !== 403) {
        logger.error(`Failed to check lock status for recording ${recordingId}:`, error);
      }
      // Safe default: assume not locked if we can't verify
      return null;
    }
  }

  /**
   * Get all active locks for a user
   *
   * @param userId - User ID
   * @returns Array of active locks
   */
  async getUserLocks(userId: string): Promise<ProcessingLock[]> {
    try {
      const { data, error } = await supabase
        .from('processing_locks')
        .select('*')
        .eq('user_id', userId)
        .gt('expires_at', new Date().toISOString());

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      logger.error(`Failed to get locks for user ${userId}:`, error);
      return [];
    }
  }

  /**
   * Cleanup expired locks (TTL-based expiration)
   *
   * Runs periodically to remove locks that have exceeded their timeout
   */
  async cleanupExpiredLocks(): Promise<number> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return 0;

    const cutoff = new Date().toISOString();
    const deleteExpired = () =>
      supabase
        .from('processing_locks')
        .delete()
        .lt('expires_at', cutoff)
        .select();

    try {
      let { data, error } = await deleteExpired();

      // This timer fires while the tab is idle, so it is often the first request
      // on a dropped keep-alive connection (net::ERR_CONNECTION_CLOSED). The
      // resilient fetch wrapper never re-sends DELETEs, but deleting rows that
      // already expired before a fixed cutoff is safe to repeat, so retry once here.
      if (error && isSupabaseTransportError(error)) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        ({ data, error } = await deleteExpired());
      }

      if (error) {
        if (isSupabaseTransportError(error)) {
          // Still offline / connection flapping; the next tick will retry.
          logger.debug('Skipped expired lock cleanup: connection unavailable');
          return 0;
        }
        throw error;
      }

      const deletedCount = data?.length || 0;

      if (deletedCount > 0) {
        logger.info(`Cleaned up ${deletedCount} expired lock(s)`);
      }

      return deletedCount;
    } catch (error) {
      logger.error('Failed to cleanup expired locks:', error);
      return 0;
    }
  }

  /**
   * Force release all locks for this instance (cleanup on logout/tab close)
   *
   * @param userId - User ID
   */
  async releaseAllInstanceLocks(userId: string): Promise<void> {
    const instanceId = getInstanceId(userId);

    try {
      logger.info(`Releasing all locks for instance ${instanceId}...`);

      const { error } = await supabase
        .from('processing_locks')
        .delete()
        .eq('processing_instance_id', instanceId);

      if (error) {
        throw error;
      }

      this.activeLocks.clear();
      logger.info(`✅ Released all locks for instance ${instanceId}`);
    } catch (error) {
      logger.error(`Failed to release all locks for instance ${instanceId}:`, error);
      localLogger.captureError(error as Error, {
        component: 'distributed_lock_manager',
        operation: 'release_all_locks',
        metadata: { instanceId }
      });
    }
  }

  /**
   * Get the instance ID for this browser tab
   *
   * @param userId - User ID
   * @returns Instance ID
   */
  getInstanceId(userId: string): string {
    return getInstanceId(userId);
  }

  /**
   * Get count of active locks
   */
  getActiveLockCount(): number {
    return this.activeLocks.size;
  }

  /**
   * Clear local lock tracking (for debugging)
   */
  clearLocalTracking(): void {
    this.activeLocks.clear();
  }
}

export const distributedLockManager = DistributedLockManager.getInstance();
